import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Users, Share2, IndianRupee, Languages, MessageCircle } from "lucide-react";
import { CONTACT } from "@/data/siteData";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";

const PARTNER_VIDEOS = {
    hindi: { label: "हिंदी", url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_trip-builder-142/artifacts/e8d24f2d40a40e31_GhumneChaley%20Partner%20Community%20v3.mp4" },
    english: { label: "English", url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_trip-builder-142/artifacts/bdc26a3798af8dcd_GhumneChaley%20Partner%20Community%20v4.mp4" },
};

function PartnerVideoSection() {
    const [lang, setLang] = useState("hindi");
    const v = PARTNER_VIDEOS[lang];
    return (
        <section className="bg-navy-deep" data-testid="partner-video-section">
            <div className="max-w-4xl mx-auto px-6 py-14 md:py-16 text-center">
                <p className="font-script text-gold text-xl leading-none">watch before you join</p>
                <h2 className="mt-2 font-display font-black text-2xl sm:text-3xl text-white">Meet the Partner Community</h2>
                <p className="mt-2 text-sm text-white/60">A quick film on how our associates earn with Ghumne Chaley — in the language you prefer.</p>
                <div className="mt-5 inline-flex rounded-full bg-white/10 p-1" data-testid="video-lang-toggle">
                    {Object.entries(PARTNER_VIDEOS).map(([k, cfg]) => (
                        <button key={k} onClick={() => setLang(k)} data-testid={`video-lang-${k}`}
                            className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-black uppercase tracking-wider transition-colors ${lang === k ? "bg-saffron text-white" : "text-white/70 hover:text-white"}`}>
                            <Languages className="h-3.5 w-3.5" /> {cfg.label}
                        </button>
                    ))}
                </div>
                <div className="mt-6 rounded-2xl overflow-hidden border border-white/15 shadow-2xl bg-black" data-testid="partner-video-player">
                    <video key={lang} controls playsInline preload="metadata"
                        poster="/partners/partner-community-cover.jpg"
                        className="w-full aspect-video object-cover" data-testid={`partner-video-${lang}`}>
                        <source src={v.url} type="video/mp4" />
                        Your browser doesn't support video — <a href={v.url} className="underline text-saffron">download the {v.label} film</a>.
                    </video>
                </div>
                <a href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi! I watched the partner community video and want to know more.")}`}
                    target="_blank" rel="noreferrer" data-testid="video-wa-cta"
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-xs font-display font-black uppercase tracking-wider text-white hover:bg-white/10 transition-colors">
                    <MessageCircle className="h-4 w-4" /> Questions? WhatsApp us
                </a>
            </div>
        </section>
    );
}

const ROLES = [
    {
        id: "explorer", badge: "/badges/explorer.png",
        name: "Ghumne Chaley Explorer", tag: "For our travellers",
        desc: "Been on a trip with us? Turn your travel stories into rewards. Share approved packages with your circle using your personal Explorer code.",
        points: ["Self-register in 2 minutes", "Unique Explorer ID & referral code", "Promote all approved packages", "Track every enquiry you bring"],
        cta: "Register as Explorer", self: true,
    },
    {
        id: "insider", badge: "/badges/insider.png",
        name: "Ghumne Chaley Travel Insider", tag: "For travel planners & creators",
        desc: "Know routes, stays and experiences worth sharing? Submit your own package ideas for approval and promote our catalogue too.",
        points: ["Self-register in 2 minutes", "Unique Insider ID & referral code", "Submit your own packages for approval", "Promote all approved packages"],
        cta: "Register as Insider", self: true,
    },
    {
        id: "ambassador", badge: "/badges/ambassador.png",
        name: "Ghumne Chaley Travel Ambassador", tag: "By invitation",
        desc: "Our on-ground champions in schools, colleges and corporates. Ambassadors are onboarded personally by our team with assigned packages.",
        points: ["Account created by our team", "Unique Ambassador ID & referral code", "Promote approved packages", "Submit packages for approval", "Assigned-package focus where applicable"],
        cta: "Request via our team", self: false,
    },
    {
        id: "privilege_member", badge: "/badges/privilege_member.png",
        name: "Ghumne Chaley Privilege Member", tag: "By invitation",
        desc: "An exclusive circle for premium & luxury travel. Privilege Members get access to our authorized premium inventory and concierge support.",
        points: ["Account created by our team", "Unique Privilege ID & referral code", "Premium & luxury packages only", "Submit packages for approval", "Dedicated concierge support"],
        cta: "Request via our team", self: false,
    },
];

const STEPS = [
    { icon: BadgeCheck, title: "Get your Associate ID", desc: "Once approved, you receive a unique ID — it doubles as your referral code." },
    { icon: Share2, title: "Share packages", desc: "Share your personal package links, or ask travellers to enter your code while booking." },
    { icon: Users, title: "Join your community", desc: "Every category has its own WhatsApp group for updates, training and support." },
    { icon: IndianRupee, title: "Track your impact", desc: "Your dashboard shows clicks, enquiries and bookings credited to you." },
];

export default function PartnersPage() {
    return (
        <div className="min-h-screen bg-cream" data-testid="partners-page">
            <Header />
            {/* hero */}
            <section className="bg-navy text-white">
                <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
                    <p className="text-xs font-black uppercase tracking-[0.25em] text-saffron" data-testid="partners-kicker">Associates & Partners</p>
                    <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl" data-testid="partners-heading">
                        Grow with Ghumne Chaley
                    </h1>
                    <p className="mt-4 max-w-2xl text-base md:text-lg text-white/70">
                        Four ways to partner with us — from sharing trips you loved, to planning new ones,
                        to representing premium travel. Every partner gets a unique ID, personal tracking
                        dashboard and a private community.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a href="#roles" data-testid="partners-explore-cta" className="inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-saffron-dark transition-colors">
                            Explore the roles <ArrowRight className="h-4 w-4" />
                        </a>
                        <Link to="/partner/login" data-testid="partners-login-cta" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-white/10 transition-colors">
                            Partner login
                        </Link>
                    </div>
                </div>
            </section>

            {/* partner community videos — Hindi & English */}
            <PartnerVideoSection />

            {/* role cards */}
            <section id="roles" className="max-w-6xl mx-auto px-6 py-14 md:py-20">
                <div className="grid md:grid-cols-2 gap-6">
                    {ROLES.map((r) => (
                        <div key={r.id} className="rounded-2xl border border-stone-200 bg-white p-7 shadow-sm hover:shadow-md transition-shadow" data-testid={`role-card-${r.id}`}>
                            <div className="flex items-start justify-between">
                                <img src={r.badge} alt={`${r.name} badge`} loading="lazy" className="h-20 w-auto object-contain drop-shadow-sm" data-testid={`role-badge-${r.id}`} />
                                <span className="rounded-full bg-stone-100 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-stone-500">{r.tag}</span>
                            </div>
                            <h2 className="mt-4 font-display font-black text-xl text-navy">{r.name}</h2>
                            <p className="mt-2 text-sm text-stone-600 leading-relaxed">{r.desc}</p>
                            <ul className="mt-4 space-y-1.5">
                                {r.points.map((p) => (
                                    <li key={p} className="flex items-start gap-2 text-sm text-stone-700">
                                        <BadgeCheck className="h-4 w-4 mt-0.5 shrink-0 text-green-600" /> {p}
                                    </li>
                                ))}
                            </ul>
                            {r.self ? (
                                <Link to={`/partners/register?role=${r.id}`} data-testid={`role-cta-${r.id}`}
                                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-display font-black uppercase tracking-wider text-white hover:bg-navy-light transition-colors">
                                    {r.cta} <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                            ) : (
                                <a href={`mailto:ghumnechaleyholidays@gmail.com?subject=${encodeURIComponent(`${r.name} — membership request`)}&body=${encodeURIComponent(`Hi Team Ghumne Chaley,\n\nI'd like to request onboarding as a ${r.name}.\n\nName: \nMobile: \nCity: \nRelevant experience: \n\nThank you!`)}`}
                                    data-testid={`role-cta-${r.id}`}
                                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-navy px-5 py-2.5 text-xs font-display font-black uppercase tracking-wider text-navy hover:bg-navy hover:text-white transition-colors">
                                    {r.cta} <ArrowRight className="h-3.5 w-3.5" />
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* how it works */}
            <section className="bg-white border-y border-stone-200">
                <div className="max-w-6xl mx-auto px-6 py-14">
                    <h2 className="font-display font-black text-2xl md:text-3xl text-navy" data-testid="partners-how-heading">How it works</h2>
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {STEPS.map((s, i) => (
                            <div key={s.title} className="relative">
                                <span className="text-[11px] font-black text-saffron">0{i + 1}</span>
                                <s.icon className="mt-2 h-6 w-6 text-navy" />
                                <h3 className="mt-2 font-bold text-navy">{s.title}</h3>
                                <p className="mt-1 text-sm text-stone-600">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-10 text-xs text-stone-400">
                        Questions? WhatsApp us at {CONTACT.phonePrimary} — GhumneChaley.com, a brand of BigKreation Entertainment Pvt. Ltd.
                    </p>
                </div>
            </section>
            <Footer />
        </div>
    );
}
